import { useEffect, useRef, useState } from 'react';
import { useViewer } from '@/features/session';
import { notify } from '@/shared/lib/toast';
import type { InvoiceData, PaddleConfigData } from '../types';
import { useInvoicePdf } from './use-invoice-pdf';
import { usePaddleCheckout } from './use-paddle-checkout';
import { usePayInvoice } from './use-pay-invoice';
import { useUpdatePaymentMethod } from './use-update-payment-method';

type BillingAction = 'update-method' | { invoiceId: string } | null;

type UseBillingActionsResult = {
  isUpdatingMethod: boolean;
  payingInvoiceId: string | null;
  updatePaymentMethod: () => Promise<void>;
  payInvoice: (invoice: InvoiceData) => Promise<void>;
  openInvoicePdf: (invoice: InvoiceData) => Promise<void>;
};

const PAYMENTS_OFF = 'Payments are not switched on yet. Write to info@macswarm.com.';

/** Billing page actions that open Paddle's overlay: a new card, or paying an unpaid invoice. */
export function useBillingActions(paddle: PaddleConfigData | null): UseBillingActionsResult {
  const [action, setAction] = useState<BillingAction>(null);
  const { refreshViewer } = useViewer();
  const checkout = usePaddleCheckout(paddle);
  const { updatePaymentMethod: startUpdate } = useUpdatePaymentMethod();
  const { payInvoice: startPayment } = usePayInvoice();
  const { invoicePdfUrl } = useInvoicePdf();
  const { phase, reset } = checkout;
  const previousPhase = useRef(phase);

  useEffect(() => {
    const wasOpen = previousPhase.current !== 'idle';
    previousPhase.current = phase;
    if (phase === 'completed') {
      notify.success(
        action === 'update-method' ? 'Payment method updated.' : 'Payment received. Thank you.',
      );
      void refreshViewer();
      setAction(null);
      reset();
    } else if (phase === 'declined' || (phase === 'idle' && wasOpen)) {
      setAction(null);
    }
  }, [action, phase, refreshViewer, reset]);

  const open = async (start: () => Promise<{ transactionId: string | null }>) => {
    const outcome = await start();
    if (!outcome.transactionId) {
      setAction(null);
      notify.error(PAYMENTS_OFF);
      return;
    }
    await checkout.openOverlay(outcome.transactionId);
  };

  return {
    isUpdatingMethod: action === 'update-method',
    payingInvoiceId: action && action !== 'update-method' ? action.invoiceId : null,
    updatePaymentMethod: async () => {
      setAction('update-method');
      await open(startUpdate);
    },
    payInvoice: async (invoice) => {
      setAction({ invoiceId: invoice.id });
      await open(() => startPayment(invoice.id));
    },
    openInvoicePdf: async (invoice) => {
      const url = await invoicePdfUrl(invoice.id);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else notify.error('The PDF is not ready yet. Try again in a minute.');
    },
  };
}
